/**
 * @fileoverview Componente Molécula para seleccionar el tipo de respuesta rápida (texto, imagen, audio, video, etc.) en el módulo de Fast Answers.
 * Cumple con Atomic Design (Molecule) y optimizaciones de renderizado (React.memo, useCallback, useMemo).
 *
 * @module FastAnswerTypeSelectMC
 * @category Component - Molecule
 * @since 2.0.0
 * @version 3.1.0
 */

import React, { memo, useMemo, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import DropdownMC from "../../components/DropdownMC";
import { iconType } from "./utils/answerType";
import CustomIconMC from "../../components/CustomIconMC";

/**
 * @typedef {Object} FastAnswerTypeSelectMCProps
 * @description Props del componente FastAnswerTypeSelectMC.
 * @property {string} answerType - Tipo de respuesta seleccionada ('all', 'text', 'audio', etc.).
 * @property {function} handleSelect - Callback para seleccionar un tipo de respuesta.
 * @property {string} [placeholder] - Placeholder personalizado para el selector.
 * @property {string} [className] - Clases CSS adicionales.
 * @property {string} [label] - Etiqueta personalizada para el selector.
 */

/**
 * Construye una opción de tipo de respuesta para el selector.
 * @function buildTypeOption
 * @memberof FastAnswerTypeSelectMC
 * @param {string} value - Valor del tipo de respuesta
 * @param {string} text - Texto a mostrar
 * @returns {Object} Opción para el selector
 */

const buildTypeOption = (value, text) => ({
  id: value,
  value,
  label: text,
  icon: iconType(value)
});


/**
 * Componente Molécula para seleccionar el tipo de respuesta rápida (texto, imagen, audio, video, etc.).
 *
 * @component FastAnswerTypeSelectMC
 * @category Component - Molecule
 *
 * @description
 * **Características:**
 * - Permite seleccionar el tipo de respuesta rápida a mostrar.
 * - Renderiza opciones con iconos personalizados.
 * - Optimizado con React.memo, useCallback y useMemo.
 *
 * **Atomic Design:**
 * - **Nivel:** Molecule
 * - **Composición:** Usa átomos (DropdownMC, CustomIconMC)
 *
 * @param {FastAnswerTypeSelectMCProps} props - Propiedades del componente
 * @returns {JSX.Element} Selector de tipo de respuesta rápida
 *
 * @example
 * <FastAnswerTypeSelectMC
 *   answerType="all"
 *   handleSelect={(type) => {}}
 * />
 *
 * @see {@link DropdownMC} Selector desplegable reutilizable
 * @see {@link CustomIconMC} Icono personalizado para cada tipo
 *
 * @since 2.0.0
 * @version 3.1.0 - Documentación JSDoc completa y optimización de performance
 */
const FastAnswerTypeSelectMC = memo(({
  answerType,
  handleSelect,
  placeholder,
  className = '',
  label
}) => {
  const { t } = useTranslation('FastAnswer');

  /**
   * Opciones de tipo de respuesta con iconos y etiquetas traducidas.
   * @type {Array<Object>}
   */
  const messageTypeOptions = useMemo(() => [
    buildTypeOption('all', t('all')),
    buildTypeOption('text', t('text')),
    buildTypeOption('text_image', t('typeImage')),
    buildTypeOption('text_pdf', t('document')),
    buildTypeOption('audio', t('typeAudio')),
    buildTypeOption('video', t('typeVideo'))
  ], [t]);

  /**
   * Placeholder computado (traducido o personalizado).
   * @type {string}
   */
  const computedPlaceholder = useMemo(() => {
    return placeholder || t('selectType');
  }, [placeholder, t]);

  /**
   * Etiqueta computada (traducida o personalizada).
   * @type {string}
   */
  const computedLabel = useMemo(() => {
    return label || t('answerType');
  }, [label, t]);

  /**
   * Callback memoizado para manejar la selección de tipo.
   * @function memoizedHandleSelect
   * @memberof FastAnswerTypeSelectMC
   * @param {Object} selectedOption - Opción seleccionada
   */
  const memoizedHandleSelect = useCallback((selectedOption) => {
    handleSelect && handleSelect(selectedOption);
  }, [handleSelect]);

  /**
   * Renderiza una opción del selector con icono y etiqueta.
   * @function renderTypeOption
   * @memberof FastAnswerTypeSelectMC
   * @param {Object} option - Opción a renderizar
   * @returns {JSX.Element}
   */
  const renderTypeOption = useCallback((option) => (
    <div className="tw-flex tw-items-center tw-w-full">
      <div className="tw-mr-3 tw-flex-shrink-0">
        <CustomIconMC
          name={option.icon}
          size="base"
          className="tw-text-gray-400"
        />	
      </div>
      <span className="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate">
        {option.label}
      </span>
    </div>
  ), []);

  /**
   * Renderiza la opción seleccionada en el selector.
   * @function renderSelectedType
   * @memberof FastAnswerTypeSelectMC
   * @param {Object} option - Opción seleccionada
   * @returns {JSX.Element}
   */
  const renderSelectedType = useCallback((option) => (
    <div className="tw-flex tw-items-center tw-w-full">
      {option && renderTypeOption(option)}
      {!option && (
        <span className="tw-text-sm tw-text-gray-500">{computedPlaceholder}</span>
      )}
    </div>
  ), [renderTypeOption, computedPlaceholder]);

  return (
    <div className="tw-wrapper tw-relative tw-mt-2 tw-mb-2">
      <DropdownMC
        id="answer-type-select"
        name="answer_type"
        label={computedLabel}
        options={messageTypeOptions}
        defaultValue={answerType}
        onSelect={memoizedHandleSelect}
        placeholder={computedPlaceholder}
        className={className}
        useFloatingLabel
        renderOption={renderTypeOption}
        renderSelected={renderSelectedType}
        search={false}
      />
    </div>
  );
});

FastAnswerTypeSelectMC.displayName = 'FastAnswerTypeSelectMC';

export default FastAnswerTypeSelectMC;
